import type { TimelinePoint } from '../api';

interface TimelineProps {
  points: TimelinePoint[];
  empty?: boolean;
  loading?: boolean;
  error?: boolean;
  refreshLabel: string;
  label?: string;
  sourceLabel?: string;
}

function statusLabel(status: TimelinePoint['status']): string {
  switch (status) {
    case 'healthy':
      return '正常';
    case 'degraded':
      return '降级';
    case 'failed':
      return '错误';
    case 'cooldown':
      return '冷却';
    case 'idle':
      return '空闲';
    default:
      return status;
  }
}

function timeLabel(checkedAt: string): string {
  const checked = new Date(checkedAt);
  if (Number.isNaN(checked.getTime())) return '未知时间';
  return checked.toLocaleTimeString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });
}

function pointTitle(point: TimelinePoint): string {
  const parts = [timeLabel(point.checkedAt), statusLabel(point.status)];
  const latency = point.ttftMs ?? point.pingMs;
  if (point.status !== 'idle') {
    parts.push(latency === null ? '未采集' : `${latency}ms`);
  }
  return parts.join(' · ');
}

function pointStatusClass(point: TimelinePoint): string {
  if (point.status === 'failed' && point.ttftMs === null && point.pingMs === null) {
    return 'status-uncollected';
  }
  return `status-${point.status}`;
}

export function Timeline({ points, empty = false, loading = false, error = false, label = '近 60 次记录', sourceLabel }: TimelineProps) {
  const recentPoints = points.slice(-60);
  const slots: Array<TimelinePoint | null> = empty || recentPoints.length === 0
    ? []
    : [...Array<TimelinePoint | null>(Math.max(0, 60 - recentPoints.length)).fill(null), ...recentPoints];
  const latestPoint = recentPoints.at(-1);
  const latestLabel = latestPoint
    ? `最近${sourceLabel ?? '记录'} ${timeLabel(latestPoint.checkedAt)}`
    : '';

  return (
    <div className="timeline-wrap" aria-label={label}>
      <div className="timeline-head">
        <span>{label}</span>
        <span>{latestLabel}</span>
      </div>
      {slots.length === 0 ? (
        <div className="timeline-empty">{loading ? '正在加载...' : error ? '加载失败，稍后重试' : '暂无监控记录'}</div>
      ) : (
        <>
          <div className="timeline" aria-hidden="true">
            {slots.map((point, index) => (
              point ? (
                <span
                  className={`timeline-bar ${pointStatusClass(point)}`}
                  data-tooltip={pointTitle(point)}
                  data-testid="timeline-bar"
                  aria-label={pointTitle(point)}
                  key={`${point.checkedAt}-${index}`}
                />
              ) : (
                <span
                  className="timeline-bar status-empty"
                  data-tooltip="空闲 · 无请求"
                  data-testid="timeline-placeholder"
                  key={`placeholder-${index}`}
                  aria-label="空闲，无请求"
                />
              )
            ))}
          </div>
          <div className="timeline-axis">
            <span>PAST</span>
            <span>NOW</span>
          </div>
        </>
      )}
    </div>
  );
}
